import React from 'react';
import { useStore } from '../../../../stores/root';

// assets
import { faCheck } from '@fortawesome/pro-light-svg-icons';

// services
import OrderService from '../../../../services/orders/order';
import ValidationService from '../../../../services/validation';

// props
import { IOrderResourceShortProps } from '../../../../props/orders/order';

// components
import { DefaultButton, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import Label from '../../../typography/label';
import Text from '../../../typography/text';

interface ICancelOrderFormProps {
    order: IOrderResourceShortProps;
    onDismissed(refresh?: boolean): void;
}

type FormDataProps = {
    reason: string;
}

type FormDataErrorProps = {
    reason?: string;
}

const CancelOrderForm: React.FC<ICancelOrderFormProps> = (props: ICancelOrderFormProps) => {
    const { banner } = useStore();
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [data, setData] = React.useState<FormDataProps>({
        reason: ""
    });
    const [error, setError] = React.useState<FormDataErrorProps>({});

    const _onSubmit = async () => {
        try {
            setSubmitting(true);
            await OrderService.cancel(props.order.id, data.reason)

            banner.add({
                key: 'cancel_order_success',
                variant: 'success',
                icon: faCheck,
                text: `Order "${OrderService.getOrderNumber(props.order.orderNumber)}" cancelled successfully`
            });
            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
        }
    }

    const isSubmitButtonDisabled = (): boolean => {
        if (error.reason) {
            return true;
        } else if (data.reason === '') {
            return true;
        }

        return false;
    }

    return <Panel headerText={"Cancel Order"}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={"Submit"} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Cancelling order ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 15 }} styles={{ root: { marginTop: 20 } }}>
            <Text>Are you sure you want to cancel order with details below?</Text>
            <Stack.Item>
                <Label>Order Number</Label>
                <Text>{OrderService.getOrderNumber(props.order.orderNumber)}</Text>
            </Stack.Item>
            <Stack horizontal tokens={{ childrenGap: 20 }}>
                <Stack.Item styles={{ root: { width: '50%' } }}>
                    <Label>Company</Label>
                    <Text>{props.order.company?.name || "-"}</Text>
                </Stack.Item>
                <Stack.Item styles={{ root: { width: '50%' } }}>
                    <Label>Customer (PIC)</Label>
                    <Text>{props.order.customer.name || "-"}</Text>
                </Stack.Item>
            </Stack>
            <Stack.Item>
                <Label>Status</Label>
                <Text>{props.order.status.name}</Text>
            </Stack.Item>
            <Stack.Item>
                <TextField label={"Reason"}
                    required={true}
                    value={data.reason}
                    rows={3}
                    multiline
                    autoAdjustHeight
                    resizable={false}
                    onChange={(evt, value) => {
                        data.reason = value || "";

                        const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 100 });
                        error.reason = validation.message;

                        setData({ ...data });
                        setError({ ...error });
                    }}
                    errorMessage={error.reason}
                    disabled={submitting} />
            </Stack.Item>
        </Stack>
    </Panel>
};

export default CancelOrderForm;
